<template>
  <div v-bind:style="{
    'padding-bottom': (!$vuetify.breakpoint.mdAndUp && (detailView && !publicView))
      ? '56px'
      : 'initial',
  }">
    <list-navigator
      v-if="detailView && !publicView"
      class="mx-md-5 mx-lg-10 mx-xl-10 maxw-1750"
      :backButtonHref="previousPath"
      :disabled="detailViewData.loading"
      :total="listViewData.items.length"
      :current="currentItemIndex"
      @navigate="(idx) => viewItemDetails(idx)"
      @share="handleShare"
      :isKI=true
    />

    <router-view v-on:request="request" v-bind="viewData" />
    <ShareDialog
      :visible="shareDialogOpen"
      @input="shareDialogOpen = false"
      :item="sharedItem"
      :positionX="sharedDialogPosition.positionX"
      :positionY="sharedDialogPosition.positionY"
    />
  </div>
</template>

<script>
import { ListNavigator, ShareDialog } from '@/components';
import KnowledgeItemsListView from './KnowledgeItemsListView.vue';
import KnowledgeItemsDetailView from './KnowledgeItemsDetailView.vue';

export {
  KnowledgeItemsListView,
  KnowledgeItemsDetailView,
  ShareDialog,
};

export default {
  name: 'KnowledgeItemsView',
  props: {
    publicView: {
      type: Boolean,
      default: () => false,
    },
  },
  components: { ListNavigator, ShareDialog },
  data() {
    return {
      listViewData: {
        lastSearchArgs: {},
        items: [],
        listType: null,
        loading: false,
      },
      detailViewData: {
        item: null,
        backButton: false,
        loading: false,
      },
      currentView: null,
      transitionName: null,
      previousPath: '/knowledge-items',
      sharedDialogPosition: {},
      shareDialogOpen: false,
    };
  },
  computed: {
    listView() {
      return this.currentView === 'list';
    },
    detailView() {
      return this.currentView === 'detail';
    },
    currentItemIndex() {
      const currentItem = this.detailViewData.item;
      if (currentItem == null) {
        return -1;
      }
      return this.listViewData.items.findIndex((item) => item.id === currentItem.id);
    },
    viewData() {
      let data = {};
      if (this.listView) {
        data = this.listViewData;
      } else if (this.detailView) {
        data = this.detailViewData;
      }
      return data;
    },
    sharedItem() {
      const { item } = this.detailViewData;
      if (item == null) return null;

      const fullShareUrl = `${process.env.VUE_APP_EXTERNAL_URL}${process.env.VUE_APP_BASE_URL_MICS_SSPA}knowledge-items/${item.id}`;

      return {
        text: `${this.$t('components.share.subjectBody.knowledge')} ${item.name}`,
        url: fullShareUrl,
        formName: item.name,
        type: 'KI',
      };
    },
  },
  methods: {
    async searchKnowledgeItems({ filters, page }) {
      const data = this.listViewData;

      data.loading = true;
      try {
        const { lastSearchArgs } = data;

        // we will make the request if filters have changed
        const mustRequest = JSON.stringify(lastSearchArgs.filters) !== JSON.stringify(filters);

        if (mustRequest) {
          if (page === 0) {
            data.items = [];
          }

          // generic search request
          const { text } = (filters || {});
          if (text == null || text.trim() === '') {
            // let response = await this.client.ticketRelated(0, LIST_PAGE_SIZE, null, 'hits');
            // data.listType = 'related';
            // if (response.total === 0) {
            //   response = await this.client.mostVisited(0, 10, null, 'hits');
            //   data.listType = 'mostVisited';
            // }
            // data.items = response.list;

            data.items = [];
            data.listType = null;
            data.lastSearchArgs = { filters: { text }, page, total: 0 };
          } else {
            // const response1 = await this.client.searchByText(0, LIST_PAGE_SIZE, text);
            const response = await this.client.searchKnowledgebyText({ text });
            data.items = response.list;
            data.listType = 'filtered';
            data.lastSearchArgs = { filters: { text }, page, total: response.total };
          }
        }
      } catch (ex) {
        console.error('error getting knowledge items page (filters=', filters, ')', ex);
      }
      this.detailViewData.backButton = data.items.length > 0;
      data.loading = false;
    },
    viewItemDetails(index) {
      const item = this.listViewData.items[index];
      this.getKnowledgeItemDetail({ id: item.id });
      this.$router.push(`/knowledge-items/${item.id}`);
    },
    async getKnowledgeItemDetail({ id }) {
      console.log('getting knowledge item detail with id ', id);

      const data = this.detailViewData;
      const currentList = this.listViewData.items;

      try {
        data.loading = true;

        const baseUrl = this.$router.options.base;
        const req = this.client.getDetail(id, baseUrl, this.publicView);
        const response = await req;
        data.item = req.format(response).unique();
        this.$root.$emit('setItemData', req.format(response).unique());

        const listIndex = currentList.findIndex((item) => item.id === data.item.id);
        if (listIndex !== -1) {
          currentList[listIndex] = data.item;
          // eslint-disable-next-line no-underscore-dangle
        }

        if (this.publicView && !this.$store.getters['session/isSignedIn'] && data?.item?.zVisibleInternet === 0) {
          this.$router.push(`/knowledge-items/${data.item.id}`);
        }

        if (!this.publicView && this.$store.getters['session/isSignedIn']) {
          this.client.hit(id) // increment item hits async (only for signed in users)
            .then((newHits) => {
              if (listIndex !== -1) {
                currentList[listIndex].hits = newHits;
              } else {
                this.detailViewData.item.hits = newHits;
              }
            }, (err) => {
              console.error('error increasing item hits', err);
            });
        }
      } catch (ex) {
        // TODO map error in details view
        console.error('error loading ticket details info', ex);
      }
      data.loading = false;
    },
    request({ type, args }) {
      this.currentView = type;
      switch (type) {
      case 'list':
        if (args.filters.text) {
          this.previousPath = `/knowledge-items?text=${args.filters.text}`;
        } else {
          this.previousPath = '/knowledge-items';
        }
        this.searchKnowledgeItems(args);
        break;
      case 'detail':
        this.getKnowledgeItemDetail(args);
        break;
      default: console.warn(`some KnowledgeItemView children requested unmapped data (${type})`);
      }
    },
    handleShare() {
      const { item } = this.detailViewData;
      if (item == null) return;

      const isMobile = this.$store.getters['globalState/get']('deviceOS') != null;

      if (isMobile === true && window.flutter_inappwebview) {
        const fullShareUrl = `${process.env.VUE_APP_EXTERNAL_URL}${process.env.VUE_APP_BASE_URL_MICS_SSPA}knowledge-items/${item.id}`;
        const args = {
          name: this.$t('components.share.mobile.knowledgeItems'),
          url: fullShareUrl,
          formName: item.name,
          type: 'KI',
        };
        window.flutter_inappwebview.callHandler('shareDataUsingNative', args);
        return;
      }

      this.shareDialogOpen = true;
    },
  },
  created() {
    this.client = this.$api.getClient('knowledgeItems');
  },
};
</script>
